import React, { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import {
    Button,
    createStyles,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormHelperText,
    InputLabel,
    LinearProgress,
    makeStyles,
    MenuItem,
    Select,
    TextField,
    Theme,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { UserListDto } from '../../../api/user';
import { DictionaryDto } from '../../../api/dictionary';
import { fetchStaffActiveCouriers } from '../../../api/couriers';
import { fetchRegionsDict } from '../../../api/regions';
import { assignNPOCourier, NPODto } from '../../../api/npo';
import * as Yup from 'yup';
import { Field, Form, Formik } from 'formik';
import Box from '@material-ui/core/Box';
import FormikTextField from '../../atoms/FormikInput/FormikInput';
import _ from 'lodash';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        formControl: {
            minWidth: 120,
            width: '100%',
            marginTop: theme.spacing(2),
        },
    })
);

interface Props {
    open: boolean;
    onClose: () => void;
    parentRegionId: number | string;
    selectedItems: NPODto[];
    dep_code?: string;
    formFields: {
        [key: string]: string;
    };
    refreshList: () => void;
}

export const NPOCourierDialog = ({
    open,
    onClose,
    parentRegionId,
    selectedItems,
    formFields,
    dep_code,
    refreshList,
}: Props) => {
    const { enqueueSnackbar } = useSnackbar();
    const classes = useStyles();
    const [activeCouriers, setActiveCouriers] = useState<UserListDto[]>([]);
    const [courier, setCourier] = useState<UserListDto | null>(null);
    const [regionId, setRegionId] = useState<number | string>('');
    const [regions, setRegions] = useState<DictionaryDto[]>([]);

    const loadActiveCouriers = useCallback(
        async (value: number | string) => {
            if (value) {
                const code = regions.find((r) => r.id === value)?.code;
                const courierList = await fetchStaffActiveCouriers(code || null);
                setActiveCouriers(courierList.content);
            } else {
                setActiveCouriers([]);
            }

            setCourier(null);
        },
        [regions]
    );

    const handleRegionChange = useCallback(
        async (e: any) => {
            const value = e.target.value as string;
            setRegionId(value);
            await loadActiveCouriers(value);
        },
        [loadActiveCouriers]
    );

    const getDictionaries = useCallback(async () => {
        try {
            setRegions(await fetchRegionsDict());
        } catch (e) {
            enqueueSnackbar('Ошибка при загрузке справочников', { variant: 'error' });
        }
    }, [enqueueSnackbar]);

    useEffect(() => {
        if (open) {
            setRegionId(parentRegionId);
            loadActiveCouriers(parentRegionId);
        }
    }, [loadActiveCouriers, open, parentRegionId]);

    useEffect(() => {
        getDictionaries();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // @ts-ignore
    // @ts-ignore
    return (
        <Dialog
            open={open}
            fullWidth={true}
            onClose={() => {
                onClose();
            }}
        >
            <DialogTitle>Выберите курьера</DialogTitle>
            <DialogContent>
                <FormControl className={classes.formControl} variant="outlined" error={!regionId}>
                    <InputLabel id="product-id-label">Регион</InputLabel>
                    <Select
                        labelId="product-id-label"
                        id="product-id"
                        value={regionId}
                        label="Регион"
                        onChange={handleRegionChange}
                    >
                        {regions.length > 1 && (
                            <MenuItem value="">
                                <em>Не выбрано</em>
                            </MenuItem>
                        )}
                        {regions.map((region, index) => (
                            <MenuItem key={index} value={region.id}>
                                {region.description}
                            </MenuItem>
                        ))}
                    </Select>
                    {!regionId && <FormHelperText>Выберите регион</FormHelperText>}
                </FormControl>
                <FormControl className={classes.formControl} error={!courier?.id}>
                    <Autocomplete
                        id="courier"
                        options={activeCouriers}
                        disabled={regionId === ''}
                        getOptionLabel={(option: UserListDto) =>
                            `${option.last_name} ${option.first_name} (${option.phone_number})`
                        }
                        //@ts-ignore
                        onChange={(event: any, newValue: UserListDto | null) => {
                            setCourier(newValue);
                        }}
                        value={courier}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                error={!courier?.id}
                                label="Курьер"
                                variant="outlined"
                            />
                        )}
                    />
                    {!courier?.id && <FormHelperText>Выберите курьера</FormHelperText>}
                </FormControl>
            </DialogContent>
            <Formik
                enableReinitialize={true}
                initialValues={formFields}
                validationSchema={Yup.lazy((formFields) =>
                    Yup.object(
                        //@ts-ignore
                        _.mapValues(formFields, (_value: string | number, _key: string) => {
                            const currentItem = selectedItems.find((item) => item.code === _key);
                            const count = currentItem?.count ? currentItem.count : 0;
                            return Yup.number()
                                .typeError('Необходимо вводить только цифры')
                                .positive('Введите положительное число')
                                .required('Заполните поле')
                                .max(count, `Максимальное количество ${count}`);
                        })
                    )
                )}
                onSubmit={(values, { setSubmitting }) => {
                    assignNPOCourier({
                        region_id: regionId,
                        courier_id: courier?.id,
                        dep_code: dep_code !== undefined ? dep_code : '',
                        assigned_npos: selectedItems.map((selectedItem) => {
                            return {
                                ...selectedItem,
                                // @ts-ignore
                                count: Number(values[selectedItem.code]) as number,
                            };
                        }),
                    })
                        .then(() => {
                            setSubmitting(false);
                            onClose();
                            refreshList();
                        })
                        .catch((err) => {
                            const { message } = err.response.data;
                            enqueueSnackbar(`Ошибка при сохранении данных: ${message}`, {
                                variant: 'error',
                            });
                            setSubmitting(false);
                        });
                }}
            >
                {({ isSubmitting, isValid }) => (
                    <Form>
                        <DialogTitle>НПО</DialogTitle>
                        <DialogContent>
                            <Box display="flex" flexDirection="column">
                                {selectedItems.map((item) => {
                                    return (
                                        <Field
                                            key={item.code}
                                            component={FormikTextField}
                                            name={item.code}
                                            label={item.description}
                                        />
                                    );
                                })}
                            </Box>

                            {isSubmitting && <LinearProgress />}
                        </DialogContent>

                        <DialogActions>
                            <Button onClick={onClose}>Отмена</Button>
                            <Button
                                disabled={!(regionId && courier?.id && isValid) || isSubmitting}
                                type={'submit'}
                            >
                                Ок
                            </Button>
                        </DialogActions>
                    </Form>
                )}
            </Formik>
        </Dialog>
    );
};

export default NPOCourierDialog;
